// ChatPanelLexical.tsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useTransition,
} from 'react';
import {createPortal} from 'react-dom';
import {useChat} from './use-chat';
import type {Part, ChatMessage} from 'src/components/agentic-ui';
import {useVirtualizer} from '@tanstack/react-virtual';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  ChatBubble,
  ChatContent,
  ChatBody,
  BodyMedium,
  CHAT_BUBBLE_ORIENTATION,
  Button,
  SubTitleSmall,
  ProgressDonut,
  ChatAnchor,
  TruncatedTextWithTooltip,
  type ProgressDonutProps,
} from '@spaced-out/ui-design-system';
import {
  Editor,
  type MentionUser,
  type UploadResult,
  type EditorHandle,
} from './editor';
import {classify} from 'src/utils/classify';
import css from './chat-panel.css';

/* ===================== Types ===================== */
// Re-export types from editor for convenience
export type {MentionUser, UploadResult} from './editor';
export type PanelMode = 'floating' | 'attached' | 'fullscreen' | 'hidden';

export interface ChatPanelProps {
  chatOptions: Parameters<typeof useChat>[0];
  fetchMentionSuggestions?: (query: string) => Promise<MentionUser[]>;
  onUploadFiles?: (files: File[]) => Promise<UploadResult[]>;
  renderMessage?: (msg: ChatMessage) => React.ReactNode;
  headerSlot?: React.ReactNode;
  height?: number;
  placeholder?: string;
  dragVisibleHeaderHeight?: number;
  // Start screen configuration
  renderStartScreen?: (args: {
    onSelectPrompt: (text: string) => void;
  }) => React.ReactNode;

  defaultMode?: PanelMode; // uncontrolled default
  lastModePersistKey?: string; // persist last mode key
  defaultOpen?: boolean;
  onModeChange?: (m: PanelMode) => void;

  attachChatTo?: string | HTMLElement; // used when mode='attached'
  attachHeaderTo?: string | HTMLElement; // optional header container when mode='attached'
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  scrollRestorePersistKey?: string; // e.g. 'chatScroll'

  draggable?: boolean; // header-drag in floating mode
  initialPosition?: {x: number; y: number};
  positionPersistKey?: string; // optional persist of position
  messageHistory?: ChatMessage[];
  onReset?: () => unknown;
}

export interface ChatPanelHandle {
  setMode: (m: PanelMode) => void;
  setOpen: (o: boolean) => void;
  setPosition: (p: {x: number; y: number}) => void;
  // Open the panel (switching out of 'hidden' if needed) and send a message
  openWithMessage: (parts: Part[]) => Promise<void>;
  currentMode?: PanelMode;
}

/* ================= Message Rendering ================= */
function DefaultMessageView({msg}: {msg: ChatMessage}) {
  return (
    <div className={css.messageItem}>
      {/* <div className={css.messageMeta}>
        {msg.role} · {new Date(msg.createdAt).toLocaleTimeString()}
        {msg.status && msg.status !== 'sent' ? ` · ${msg.status}` : ''}
      </div> */}
      <div className={css.messageBody}>
        {msg.parts.map((p: Part, i: number) => {
          let key = '';
          if (p.type === 'plain_text') {
            key = `text:${(p.data || '').slice(0, 32)}`;
          } else if (p.type === 'ProgressDonut') {
            key = `${p.id}`;
          } else {
            key = `other:${i}`;
          }

          // if (p.type === 'text' && p.format === 'markdown') {
          //   return (
          //     <ReactMarkdown key={key} remarkPlugins={[remarkGfm]}>
          //       {p.text}
          //     </ReactMarkdown>
          //   );
          // }
          if (p.type === 'plain_text') {
            return (
              <ChatBubble
                orientation={
                  msg.role === 'user'
                    ? CHAT_BUBBLE_ORIENTATION.right
                    : CHAT_BUBBLE_ORIENTATION.left
                }
              >
                <ChatContent classNames={{wrapper: css.chatContentWrapper}}>
                  {msg.role === 'user' && (
                    <ChatBody>
                      <BodyMedium>{p.data}</BodyMedium>
                    </ChatBody>
                  )}
                  {msg.role === 'assistant' && (
                    <ChatBody>
                      <ReactMarkdown key={key} remarkPlugins={[remarkGfm]}>
                        {p.data}
                      </ReactMarkdown>
                    </ChatBody>
                  )}
                </ChatContent>
              </ChatBubble>
            );
          }
          if (p.type === 'thinking') {
            return (
              <ChatBubble
                orientation={
                  msg.role === 'user'
                    ? CHAT_BUBBLE_ORIENTATION.right
                    : CHAT_BUBBLE_ORIENTATION.left
                }
              >
                <ChatAnchor />
                <ChatContent classNames={{wrapper: css.chatContentWrapper}}>
                  <ChatBody isLoading loadingText="Thinking"></ChatBody>
                </ChatContent>
              </ChatBubble>
            );
          }
          if (p.type === 'ProgressDonut') {
            const data = p.data || {};
            return (
              <div key={key} className={css.progressDonutWrapper}>
                <ProgressDonut
                  key={key}
                  {...(data as unknown as ProgressDonutProps)}
                />
              </div>
            );
          }
          // if (p.type === 'widget') {
          //   try {
          //     const data = p.data || {};
          //     if (Array.isArray(data.attachments)) {
          //       return (
          //         <div key={key} className={css.attachmentList}>
          //           {data.attachments.map((a: any) => (
          //             <a
          //               key={a.id}
          //               href={a.url || '#'}
          //               onClick={(e) => {
          //                 if (!a.url) {
          //                   e.preventDefault();
          //                 }
          //               }}
          //               className={css.attachmentLink}
          //             >
          //               <span>{a.name}</span>
          //               <span className={css.muted}>
          //                 {Math.max(1, Math.round((a.size ?? 0) / 1024))}
          //                 {` ${t('KB', 'KB')}`}
          //               </span>
          //             </a>
          //           ))}
          //         </div>
          //       );
          //     }
          //     return (
          //       <pre key={key} className={css.preBlock}>
          //         {JSON.stringify(data, null, 2)}
          //       </pre>
          //     );
          //   } catch {
          //     return (
          //       <pre key={key} className={css.preBlock}>
          //         {JSON.stringify(p.data, null, 2)}
          //       </pre>
          //     );
          //   }
          // }
          if (p.type === 'action') {
            // Actions are non-visual here; nothing to render
            return null;
          }
          return null;
        })}
      </div>
    </div>
  );
}

/* ================= Scroll Restoration ================= */
function useScrollRestoration(
  scrollerRef: React.RefObject<HTMLElement | null>,
  key?: string,
) {
  useLayoutEffect(() => {
    if (!key) {
      return;
    }
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    const read = () => localStorage.getItem(key) || '0';
    const write = (y: number) => {
      localStorage.setItem(key, String(y));
    };
    requestAnimationFrame(() => {
      const y = parseInt(read(), 10);
      el.scrollTop = Number.isFinite(y) ? y : 0;
    });
    let raf = 0;
    const onScroll = () => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      raf = requestAnimationFrame(() => write(el.scrollTop));
    };
    el.addEventListener('scroll', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, [scrollerRef, key]);
}

/* ================= Draggable header (floating mode) ================= */
// eslint-disable-next-line max-params
function useDraggableFloating(
  panelRef: React.RefObject<HTMLDivElement | null>,
  headerRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  posState: {
    pos: {x: number; y: number};
    setPos: (p: {x: number; y: number}) => void;
  },
  persist?: {key?: string},
  visibleHeaderHeight: number = 20,
) {
  const sizeRef = useRef<{w: number; h: number}>({w: 0, h: 0});
  const persistKey = persist?.key;

  // Use refs to avoid re-running effect when position changes during drag
  const posRef = useRef(posState.pos);
  const setPosRef = useRef(posState.setPos);
  // Update refs during render (not in effect) to always have latest values
  posRef.current = posState.pos;
  setPosRef.current = posState.setPos;

  const save = useCallback(
    (p: {x: number; y: number}) => {
      if (!persistKey) {
        return;
      }
      localStorage.setItem(persistKey, JSON.stringify(p));
    },
    [persistKey],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const panel = panelRef.current;
    const header = headerRef.current;
    if (!panel || !header) {
      return;
    }

    const ro = new ResizeObserver(() => {
      const rect = panel.getBoundingClientRect();
      sizeRef.current = {w: rect.width, h: rect.height};
    });
    ro.observe(panel);

    let dragging = false;
    let startX = 0,
      startY = 0;
    let originX = 0,
      originY = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (
        (e.target as HTMLElement).closest(
          'button, a, input, textarea, [contenteditable="true"]',
        )
      ) {
        return;
      }
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      originX = posRef.current.x;
      originY = posRef.current.y;
      (e.target as Element).setPointerCapture?.(e.pointerId);
      document.body.style.userSelect = 'none';
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) {
        return;
      }
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const next = clampToViewport(
        {x: originX + dx, y: originY + dy},
        sizeRef.current,
        visibleHeaderHeight,
      );
      setPosRef.current(next);
      posRef.current = next;
    };
    const onPointerUp = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      save(posRef.current);
    };
    const onPointerCancel = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      save(posRef.current);
    };

    header.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    return () => {
      header.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      ro.disconnect();
    };
  }, [enabled, panelRef, headerRef, save]);
}

function clampToViewport(
  p: {x: number; y: number},
  size: {w: number; h: number},
  visibleHeaderHeight?: number,
) {
  const vw = window.innerWidth,
    vh = window.innerHeight;
  const maxX = Math.max(0, vw - size.w);
  // Allow the panel to overflow the bottom so only the header remains visible
  const headerH = Math.max(0, visibleHeaderHeight ?? size.h);
  const maxY = Math.max(0, vh - headerH);
  return {
    x: Math.min(Math.max(0, p.x), maxX),
    y: Math.min(Math.max(0, p.y), maxY),
  };
}

function getPanelStyle({
  imode,
  minimized,
  floatingFullHeight,
  height,
  pos,
}: {
  imode: PanelMode;
  minimized: boolean;
  floatingFullHeight: boolean;
  height: number;
  pos: {x: number; y: number};
}): React.CSSProperties | undefined {
  if (minimized) {
    return undefined;
  }
  const isFloating = imode === 'floating';
  const isFullscreen = imode === 'fullscreen';
  return {
    minHeight: isFullscreen
      ? '100vh'
      : isFloating && floatingFullHeight
      ? 'calc(100vh - 70px)'
      : height,
    maxHeight: isFullscreen
      ? '100vh'
      : isFloating && floatingFullHeight
      ? 'calc(100vh - 70px)'
      : height,
    width: isFloating ? 420 : '100%',
    position: isFloating ? ('fixed' as const) : ('relative' as const),
    left: isFloating ? pos.x : undefined,
    top: isFloating ? pos.y : undefined,
    zIndex: imode !== 'attached' ? 60 : 'auto',
  } as React.CSSProperties;
}

/* ================= Main Panel ================= */
export const ChatPanelLexical = forwardRef<ChatPanelHandle, ChatPanelProps>(
  (
    {
      chatOptions,
      fetchMentionSuggestions,
      onUploadFiles,
      renderMessage,
      height = 660,
      placeholder,
      dragVisibleHeaderHeight = 20,
      defaultMode = 'hidden',
      onModeChange,
      attachChatTo,
      attachHeaderTo,
      headerSlot,
      defaultOpen = true,
      onOpenChange,
      scrollRestorePersistKey = 'chatScroll',
      draggable = true,
      initialPosition = {x: 16, y: 16},
      positionPersistKey = 'chatPos',
      messageHistory = [],
      renderStartScreen,
      onReset,
      lastModePersistKey = 'chatMode',
    },
    ref,
  ) => {
    const [_isPending, startTransition] = useTransition();
    const {t} = useLabelTranslation();
    const chat = useChat(chatOptions);

    // uncontrolled mode with persistence
    const [imode, setIMode] = useState<PanelMode>(() => {
      // 1) persisted value (localStorage only)
      if (lastModePersistKey) {
        try {
          const v = (localStorage.getItem(lastModePersistKey) ||
            '') as PanelMode;
          if (
            v === 'floating' ||
            v === 'attached' ||
            v === 'fullscreen' ||
            v === 'hidden'
          ) {
            return v;
          }
        } catch {}
      }
      // 2) fallback to provided default, defaulting to hidden
      return defaultMode;
    });

    // persist mode on change (localStorage only)
    useEffect(() => {
      if (!lastModePersistKey) {
        return;
      }
      try {
        localStorage.setItem(lastModePersistKey, imode);
      } catch {}
    }, [imode, lastModePersistKey]);

    const setModeSafe = useCallback(
      (m: PanelMode) => {
        setIMode(m);
        onModeChange?.(m);
      },
      [onModeChange],
    );

    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
    const editorRef = useRef<EditorHandle | null>(null);

    // list + scroll
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    useScrollRestoration(scrollerRef, scrollRestorePersistKey);
    const rows = [...messageHistory, ...chat.snapshot.messages];
    const rowHeightsRef = useRef(new Map<number, number>());
    const virtual = useVirtualizer({
      count: rows.length,
      getScrollElement: () => scrollerRef.current,
      estimateSize: (i) => rowHeightsRef.current.get(i) ?? 80,
      overscan: 8,
    });
    const stickRef = useRef(true);
    useLayoutEffect(() => {
      const el = scrollerRef.current;
      if (!el) {
        return;
      }
      // Only auto-stick when explicitly in sticky mode.
      // This avoids fighting the user's scroll when they are near the bottom
      // while a "thinking" message is live-updating.
      if (stickRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    }, [rows]);
    const Row = ({index}: {index: number}) => {
      const msg = rows[index];
      const refRow = useRef<HTMLDivElement | null>(null);
      useLayoutEffect(() => {
        const el = refRow.current;
        if (!el) {
          return;
        }
        const h = el.getBoundingClientRect().height;
        if (rowHeightsRef.current.get(index) !== h) {
          rowHeightsRef.current.set(index, h);
          virtual.measure();
        }
      }, [index, msg]);
      return (
        <div ref={refRow} className={css.row}>
          {renderMessage ? (
            renderMessage(msg)
          ) : (
            <DefaultMessageView msg={msg} />
          )}
        </div>
      );
    };

    const onSendUserMessage = useCallback(
      async (parts: Part[]) => {
        await chat.sendMessage(parts);
      },
      [chat],
    );

    const onPrefillFromPromptAction = useCallback(
      (text: string) => {
        startTransition(() => {
          // Reveal if hidden/minimized and prefill editor
          if (imode === 'hidden') {
            setModeSafe('floating');
          }
          if (!isOpen) {
            onOpenChange ? onOpenChange(true) : setIsOpen(true);
          }
          editorRef.current?.setPlainText(text);
          editorRef.current?.focus();
        });
      },
      [imode, setModeSafe, isOpen, onOpenChange],
    );

    // header + drag
    const panelRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);

    // Lazy initialization: read persisted position or use initialPosition
    const [pos, setPos] = useState<{x: number; y: number}>(() => {
      if (!positionPersistKey) {
        return initialPosition;
      }
      const read = () => {
        const v = localStorage.getItem(positionPersistKey);
        if (!v) {
          return null;
        }
        try {
          return JSON.parse(v) as {x: number; y: number};
        } catch {
          return null;
        }
      };
      return read() || initialPosition;
    });

    const [floatingFullHeight, setFloatingFullHeight] = useState<boolean>(true);
    useDraggableFloating(
      panelRef,
      headerRef,
      draggable && imode === 'floating' && isOpen,
      {pos, setPos},
      {key: positionPersistKey},
      dragVisibleHeaderHeight,
    );

    // container for attached mode - compute during render instead of effect
    const container =
      imode === 'attached'
        ? attachChatTo instanceof HTMLElement
          ? attachChatTo
          : typeof attachChatTo === 'string'
          ? (document.querySelector(attachChatTo) as HTMLElement | null)
          : null
        : null;

    const headerContainer =
      imode === 'attached'
        ? attachHeaderTo instanceof HTMLElement
          ? attachHeaderTo
          : typeof attachHeaderTo === 'string'
          ? (document.querySelector(attachHeaderTo) as HTMLElement | null)
          : null
        : null;

    // expose imperative API
    useImperativeHandle(
      ref,
      () => ({
        setMode: setModeSafe,
        setOpen: (o: boolean) =>
          onOpenChange ? onOpenChange(o) : setIsOpen(o),
        setPosition: setPos,
        openWithMessage: async (parts: Part[]) => {
          // If hidden, reveal as floating by default
          if (imode === 'hidden') {
            setModeSafe('floating');
          }
          if (!isOpen) {
            if (onOpenChange) {
              onOpenChange(true);
            } else {
              setIsOpen(true);
            }
          }
          await chat.sendMessage(parts);
        },
        currentMode: imode,
      }),
      [setModeSafe, onOpenChange, imode, isOpen, chat],
    );

    // Hidden mode: render nothing at all (no button)
    if (imode === 'hidden') {
      return null;
    }

    const minimized = !isOpen;
    const shouldShowCloseButton = isOpen && imode !== 'attached';
    const shouldShowFullHeightButton = isOpen && imode !== 'attached';
    const heightToggleButtonIconName = floatingFullHeight
      ? 'arrow-down-left-and-arrow-up-right-to-center'
      : 'arrow-up-right-and-arrow-down-left-from-center';

    const startContent = renderStartScreen?.({
      onSelectPrompt: onPrefillFromPromptAction,
    });
    const isOnInitialPage = startContent !== null;

    const headerContent = (
      <>
        <div className={css.headerNameAndBackButtonContainer}>
          {!isOnInitialPage && (
            <Button
              type="ghost"
              iconLeftName="chevron-left"
              size="small"
              onClick={() => {
                chat.clear();
                onReset?.();
              }}
            ></Button>
          )}

          <SubTitleSmall>
            <TruncatedTextWithTooltip>
              {/* added this as header content won't be static in future */}
              {t('Ask_AI', 'Ask AI')}
            </TruncatedTextWithTooltip>
          </SubTitleSmall>
          {imode === 'attached' ? headerSlot : null}
        </div>
        <div className={css.headerControls}>
          <Button type="ghost" iconLeftName="history" size="small"></Button>
          {shouldShowFullHeightButton && (
            <Button
              type="ghost"
              size="small"
              iconLeftName={heightToggleButtonIconName}
              onClick={() => setFloatingFullHeight((v) => !v)}
              aria-label={t('Height_toggle', 'Height toggle')}
            ></Button>
          )}
          {/* <Button
          type="ghost"
          iconLeftName="map-pin"
          onClick={() => setModeSafe('attached')}
          aria-label={t('Attach', 'Attach')}
        ></Button> */}
          {/* <Button
          type="ghost"
          iconLeftName="paper-plane"
          onClick={() => setModeSafe('floating')}
          aria-label={t('Float', 'Float')}
        ></Button> */}
          {/* <Button
          type="ghost"
          iconLeftName="maximize"
          onClick={() => setModeSafe('fullscreen')}
          aria-label={t('Full', 'Full')}
        ></Button> */}

          {shouldShowCloseButton ? (
            <Button
              type="ghost"
              size="small"
              iconLeftName="close"
              onClick={(isOpen) => setIsOpen(!isOpen)}
              aria-label={t('Minimize', 'Minimize')}
            ></Button>
          ) : null}
        </div>
      </>
    );

    const header = (
      <header
        ref={headerRef}
        className={classify(css.header, {
          [css.headerNoDrag]: minimized,
        })}
      >
        {headerContent}
      </header>
    );

    const body = (
      <>
        <div
          ref={scrollerRef}
          className={css.scroller}
          onScroll={(e) => {
            const el = e.currentTarget;
            const atBottom =
              el.scrollHeight - el.scrollTop - el.clientHeight < 10;
            stickRef.current = atBottom;
          }}
        >
          {startContent !== null ? (
            <div className={css.startScreenContainer}>{startContent}</div>
          ) : (
            <div
              style={{
                height: virtual.getTotalSize(),
                width: '100%',
                position: 'relative',
              }}
            >
              {virtual.getVirtualItems().map((v) => (
                <div
                  key={v.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${v.start}px)`,
                  }}
                >
                  <Row index={v.index} />
                </div>
              ))}
            </div>
          )}
        </div>
        <footer className={css.footer}>
          <Editor
            ref={editorRef}
            placeholder={placeholder}
            fetchMentionSuggestions={fetchMentionSuggestions}
            onUploadFiles={onUploadFiles}
            onSend={onSendUserMessage}
            disableSend={chat.snapshot.connectionStatus === 'OPEN'}
            autoFocus={isOpen}
          />
        </footer>
      </>
    );

    const panel = (
      <div
        ref={panelRef}
        className={classify(css.panel, {
          [css.panelMinimized]: minimized,
        })}
        style={getPanelStyle({
          imode,
          minimized,
          floatingFullHeight,
          height,
          pos,
        })}
      >
        {minimized ? (
          <Button
            iconLeftName="sparkles"
            iconLeftType="solid"
            type="gradient"
            onClick={() => setIsOpen(true)}
            aria-label={t('Open', 'Open')}
          >
            {t('Ask_AI', 'Ask AI')}
          </Button>
        ) : (
          header
        )}

        {isOpen ? body : null}
      </div>
    );

    if (minimized) {
      return createPortal(panel, document.body);
    }
    if (imode === 'fullscreen') {
      return createPortal(
        <div style={{position: 'fixed', inset: 0, zIndex: 60}}>{panel}</div>,
        document.body,
      );
    }
    if (imode === 'floating') {
      return createPortal(panel, document.body);
    }
    if (imode === 'attached' && container) {
      // NOTE:(diwaker) In attached mode, allow host to provide separate containers for header and body
      return (
        <>
          {headerContainer
            ? createPortal(headerContent, headerContainer)
            : null}
          {container ? createPortal(body, container) : null}
        </>
      );
    }
    return panel;
  },
);
